import React, { useEffect } from 'react'

import { Box, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { Barcode } from 'src/components/Barcode'

import { convertGS1 } from './utils'

const useStyles = makeStyles({
  label: {
    width: '4in',
    height: '6in',
  },
  zoneA: {
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneB: {
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
    fontSize: '12px',
  },
  zoneC: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneD: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
    fontSize: '11px',
  },
  zoneE: {
    borderTop: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '4in',
    fontSize: '11px',
  },
  zoneF: {
    borderTop: '1px solid black',
    borderRight: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneG: {
    borderTop: '1px solid black',
    borderLeft: '1px solid black',
    borderBottom: '1px solid black',
    height: '1in',
    width: '2in',
  },
  zoneH: {
    borderTop: '1px solid black',
    height: '2in',
    width: '4in',
  },
  header: {
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '3px',
  },
  contents: {
    paddingLeft: '3px',
    lineHeight: '1.0',
  },
  address: {
    lineHeight: '1.0',
    paddingLeft: '10px',
  },
  from: {
    lineHeight: '1.0',
    fontSize: '10px',
    paddingLeft: '5px',
  },
  inline_header: {
    fontWeight: 'bold',
    paddingLeft: '5px',
  },
  to: {
    lineHeight: '1.0',
    fontWeight: 'bold',
    paddingLeft: '10px',
  },
  shipTo: {
    lineHeight: '1.0',
    fontSize: '12px',
    fontWeight: 'bold',
    paddingLeft: '6px',
  },
  container: {
    top: 'auto',
    bottom: 0,
  },
  center: {
    flexGrow: 1,
    textAlign: 'center',
  },
  inlineHeader: {
    fontWeight: 'bold',
    fontSize: '14px',
    paddingLeft: '3px',
  },
})

interface OceanStateLabelProps {
  zoneA: string[]
  zoneB: string[]
  zoneC: string
  zoneD: {
    carrier: string
    pro: string
    bol: string
    case_count: number[]
  }
  zoneE: {
    po: string
    retailer_sku: string
    vendor_part_number: string
    case_inner_quantity: number
  }
  zoneG: string
  zoneH: string
}

interface Props {
  data: OceanStateLabelProps
  onReadyToPrint?: () => void
}

export const OceanStateLabel = (props: Props) => {
  const { data, onReadyToPrint } = props
  const ssccBarcode = { barcode: convertGS1(data.zoneH, 2), type: 'gs1-128', displayName: '' }
  const shipToBarcode = { barcode: convertGS1(data.zoneC, 3), type: 'gs1-128', displayName: '' }
  const classes = useStyles()

  useEffect(() => {
    onReadyToPrint?.()
  }, [onReadyToPrint])

  return (
    <div className={`always-page-break ${classes.label}`}>
      <div className="print-page">
        <Box className={classes.label}>
          <Grid container spacing={0}>
            <Grid item>
              <Box className={classes.zoneA}>
                <Typography className={classes.header}>FROM</Typography>
                {data.zoneA.map((line) => (
                  <Typography key={line} className={classes.from}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneB}>
                <Typography className={classes.header}>TO</Typography>
                {data.zoneB.map((line) => (
                  <Typography key={line} className={classes.to}>
                    {line}
                  </Typography>
                ))}
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneC}>
                <Grid container>
                  <Grid item>
                    <Typography className={classes.header}>SHIP TO POSTAL CODE:</Typography>
                  </Grid>
                  <Grid item>
                    <Box display="flex" justifyContent="center" ml={4}>
                      <Typography variant="h6">{shipToBarcode.barcode}</Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box ml={6} mr={6}>
                  <Barcode barcode={shipToBarcode} scale={2} height={10} />
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneD}>
                <Box>
                  <span className={classes.inline_header}>Carrier: </span>
                  <span>{data.zoneD.carrier}</span>
                </Box>
                <Box>
                  <span className={classes.inline_header}>PRO #: </span>
                  <span>{data.zoneD.pro}</span>
                </Box>
                <Box>
                  <span className={classes.inline_header}>B/L Number: </span>
                  <span>{data.zoneD.bol}</span>
                </Box>
                <Box>
                  <span className={classes.inline_header}>Number of cartons: </span>
                  <span>{data.zoneD.case_count[0]} of {data.zoneD.case_count[1]}</span>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneE}>
                <Typography className={classes.header}>CONTENTS:</Typography>
                <Box>
                  <span className={classes.inline_header}>PO Number#: </span>
                  <span>{data.zoneE.po}</span>
                </Box>
                <Box>
                  <span className={classes.inline_header}>UPC#: </span>
                  <span>{data.zoneE.vendor_part_number}</span>
                </Box>
                <Box>
                  <span className={classes.inline_header}>SKU#: </span>
                  <span>{data.zoneE.retailer_sku}</span>
                </Box>
                <Box>
                  <span className={classes.inline_header}>Carton Quantity: </span>
                  <span>{data.zoneE.case_inner_quantity} each</span>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneF}></Box>
            </Grid>
            <Grid item>
              <Box className={classes.zoneG}></Box>
            </Grid>
            <Grid item>
              <Box className={`${classes.zoneH} ${classes.container}`}>
                <Typography className={classes.header}>SSCC-18</Typography>
                <Box ml={5} mr={5}>
                  <Barcode barcode={ssccBarcode} scale={2} height={20} />
                </Box>
                <Box display="flex" justifyContent="center">
                  <Typography variant="h4">{ssccBarcode.barcode}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </div>
    </div>
  )
}
